import { Runner, Time, FINISH_CODE, RunnerStatus } from '../common/types';

export function FormatCombinedPlaceAndTime(runner: Runner, code: string, raceAccuracy: number, onlyPlaceAndUsedTime: boolean) {
    let output: string = '';
    switch (runner.status) {
        case RunnerStatus.DNS:
            output = 'DNS';
            break;
        case RunnerStatus.DNF:
            output = 'DNF';
            break;
        case RunnerStatus.DSQ:
            output = 'DSQ';
            break;
        case RunnerStatus.NC:
            output = 'NC';
            break;
        default:
            if (('not_competing' in runner) && runner.not_competing) {
                output = 'NC';
            }
            break;
    }

    let time: Time = null;
    if (code in runner.times) {
        time = runner.times[code];
    }

    if (time === null) {
        return '';
    }
    if (time.running_time !== null && !onlyPlaceAndUsedTime) {
        const formattedDuration = FormatMilliseconds(time.running_time, false, 0, false);
        let out = '<div class="running-time">(';
        if (runner.not_competing) {
            out = out + 'NC - ';
        } else if (time.running_place !== null) {
            out = out + time.running_place + ' - ';
        }
        out = out + formattedDuration + ')</div>';

        if (time.running_difference != null) {
            out = out + '<div class="combined-difference">' + FormatMilliseconds(time.running_difference, false, 0, true) + '</div>';
        }
        return out;
    }

    if (time.used_time === null && runner.status === RunnerStatus.OK && code === FINISH_CODE) {
        return 'OK';
    }

    switch (runner.status) {
        case RunnerStatus.NC: {
            if (time.used_time !== null) {
                output = output + ' - ';
            }
            // fall-through
        }
        case RunnerStatus.OK:
        case RunnerStatus.REGISTERED:
            {
                if (runner.not_competing && runner.status != RunnerStatus.NC && time.used_time !== null) {
                    output = output + ' - ';
                }

                if (time.place !== null) {
                    output = output + time.place + ' - ';
                }
                output = output + FormatMilliseconds(time.used_time, code === FINISH_CODE, raceAccuracy, false);

                if (time.difference != null && !onlyPlaceAndUsedTime) {
                    output = output + '<div class="combined-difference">' + FormatMilliseconds(time.difference, code === FINISH_CODE, raceAccuracy, true) + '</div>';
                }

                break;
            }
        case RunnerStatus.DSQ:
            {
                if (time.used_time !== null) {
                    output = output + ' - ' + FormatMilliseconds(time.used_time, code === FINISH_CODE, raceAccuracy, false);
                }
                break;
            }
    }

    return output;
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;

// We could use dayjs.duration, but that is somewhere around 20x slower.
// That actually matters a bit for large races, and considering how simple the
// below implementation is we consider it to be worth it.
export function FormatMilliseconds(
    milliseconds: number,
    showSubSecond: boolean,
    raceAccuracy: number,
    addPrefixSymbol: boolean): string {
    if (milliseconds === null) {
        return '';
    }

    const negative = milliseconds < 0;
    milliseconds = Math.abs(milliseconds);

    const hours = Math.floor(milliseconds / MS_PER_HOUR);
    milliseconds -= (hours * MS_PER_HOUR);

    const minutes = Math.floor(milliseconds / MS_PER_MINUTE);
    milliseconds -= (minutes * MS_PER_MINUTE);

    const seconds = Math.floor(milliseconds / MS_PER_SECOND);
    milliseconds -= (seconds * MS_PER_SECOND);
    let formatted = '';
    if (addPrefixSymbol) {
        if (negative) {
            formatted += '-';
        } else {
            formatted += '+';
        }
    }

    if (hours > 0) {
        formatted += hours.toString().padStart(2, '0') + ':';
    }

    formatted += minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');


    if (showSubSecond && raceAccuracy > 0) {
        const ms = milliseconds.toString().padStart(3, '0');
        return formatted + ',' + ms.substring(0, raceAccuracy);
    }
    return formatted;
}

export function EscapeHtml(unsafe: string) {
    if (unsafe === null) {
        return null;
    }

    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

export function FormatPlace(runner: Runner, intermediatePointCode: string): string {
    if (!(intermediatePointCode in runner.times)) {
        return '';
    }

    const time = runner.times[intermediatePointCode];
    if (time === null) {
        return '';
    }


    if (intermediatePointCode === FINISH_CODE) {
        if (runner.status === RunnerStatus.REGISTERED) {
            return '';
        }
    } else {
        const propertiesToIgnore = {
            'running_time': true,
            'running_place': true,
            'running_difference': true
        };

        let allIsNull = true;
        for (const property in time) {
            if (time[property] !== null && !(property in propertiesToIgnore)) {
                allIsNull = false;
                break;
            }
        }


        if (allIsNull) {
            return '';
        }

        if (runner.status !== RunnerStatus.REGISTERED &&
            runner.status !== RunnerStatus.OK) {
            return '';
        }
    }

    switch (runner.status) {
        case RunnerStatus.DSQ:
            return 'DSQ';
        case RunnerStatus.DNF:
            return 'DNF';
        case RunnerStatus.DNS:
            return 'DNS';
        case RunnerStatus.NC:
            return 'NC';
        default:
            if (runner.not_competing) {
                return 'NC';
            }

            if (time.place === null) {
                return 'OK';
            }
            return time.place.toString();
    }
}

export function FormatShooting(shootings: string, numShootings: number): string {
    let output = '';
    for (let i = 0; i < numShootings; ++i) {
        if (i > 0) {
            output = output + '-';
        }

        if (i >= shootings.length) {
            output = output + 'x';
        } else {
            output = output + shootings[i];
        }
    }

    return output;
}